/**
 * Zod-based validation rules per field type.
 * Delegates to per-type schema files; single entry point for getFieldSchema, validateFieldValue, isFieldValueEmpty.
 * Character limit helpers re-exported from characterLimit for UI and validation.
 */

import { z } from 'zod';
import type { FieldSchemaConfig, ValidateFieldValueResult } from '../types';
import { BULK_EDIT_KEEP_CURRENT, BULK_EDIT_CLEAR } from '../constants';
import { getCharacterLimit, defaultDescriptionMaxChar } from './characterLimit';
import { mergeWithDefaults } from './bulkEdit';
import { getTextSchema } from './text';
import { getDescriptionSchema } from './description';
import { getNumberSchema } from './number';
import { getDropdownSchema } from './dropdown';
import { getDateSchema } from './date';
import { getMultiselectSchema } from './multiselect';
import { getLinkableSchema } from './linkable';
import { getStatusSchema } from './status';
import { getBooleanSchema } from './boolean';

export type { FieldSchemaConfig, ValidateFieldValueResult } from '../types';
export { getCharacterLimit, defaultDescriptionMaxChar } from './characterLimit';
export { mergeWithDefaults, defaultValidationConfig } from './bulkEdit';

function firstErrorMessage(result: z.SafeParseReturnType<unknown, unknown>): string | undefined {
  if (result.success) return undefined;
  const first = result.error.issues[0];
  return first ? first.message : result.error.message;
}

/**
 * Builds a Zod schema for a column's value based on its type and config.
 * Merges column config with default validation config (column takes precedence).
 */
export function getFieldSchema(col: FieldSchemaConfig): z.ZodTypeAny {
  const merged = mergeWithDefaults(col);
  const type = merged.type ?? 'text';

  switch (type) {
    case 'text':
      return getTextSchema(merged);
    case 'description':
      return getDescriptionSchema(merged);
    case 'number':
    case 'fraction':
      return getNumberSchema(merged);
    case 'dd':
    case 'tags-dd':
      return getDropdownSchema(merged);
    case 'date':
    case 'dateRange':
      return getDateSchema(merged);
    case 'multiselect':
    case 'tags':
      return getMultiselectSchema(merged);
    case 'status':
      return getStatusSchema(merged);
    case 'boolean':
    case 'switch':
      return getBooleanSchema(merged);
    default:
      return getLinkableSchema(merged);
  }
}

/**
 * Validates a single field value against the column's schema.
 * Treats bulk-edit sentinels (keep current / clear) as valid so required-field errors are not shown for unchanged or cleared fields.
 */
export function validateFieldValue(
  col: FieldSchemaConfig & { key?: string },
  value: unknown
): ValidateFieldValueResult {
  if (value === BULK_EDIT_KEEP_CURRENT) return { success: true };
  if (value === BULK_EDIT_CLEAR) return { success: true };
  const schema = getFieldSchema(col);
  const result = schema.safeParse(value);
  if (result.success) return { success: true };
  const message = firstErrorMessage(result);
  return { success: false, error: message };
}

/**
 * Returns true if the value is considered empty for this column type (for use in bulk edit / ready-for-export).
 */
export function isFieldValueEmpty(col: FieldSchemaConfig, value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object' && value !== null) {
    const o = value as Record<string, unknown>;
    return o.id === undefined || o.id === null || o.id === '';
  }
  return false;
}
